import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Select, Modal } from 'antd';
import moment from 'moment-timezone';
import { store as mainStore } from 'redux/store';
import styles from './index.module.scss';

const { Option } = Select;

/**
 * Returns the active display timezone.
 *
 * The component may be mounted under a Redux Provider that does not include
 * the `settings` reducer (e.g. VenueBuilder's local store). In that case we
 * fall back to the main app store from `redux/store.js`, subscribing so the
 * component re-renders when the canonical value changes.
 */
const useActiveTimezone = () => {
    const localSettings = useSelector((state) => state?.settings);
    const hasLocalSettingsReducer = localSettings !== undefined;
    const localTimezone = localSettings?.activeTimezone;

    const [fallbackTimezone, setFallbackTimezone] = useState(() => {
        try {
            return mainStore?.getState()?.settings?.activeTimezone;
        } catch (_e) {
            return undefined;
        }
    });

    useEffect(() => {
        if (hasLocalSettingsReducer) return undefined;
        if (!mainStore || typeof mainStore.subscribe !== 'function') {
            return undefined;
        }
        const unsubscribe = mainStore.subscribe(() => {
            try {
                const next = mainStore.getState()?.settings?.activeTimezone;
                setFallbackTimezone((prev) => (prev === next ? prev : next));
            } catch (_e) {
                /* noop */
            }
        });
        return unsubscribe;
    }, [hasLocalSettingsReducer]);

    return {
        activeTimezone: hasLocalSettingsReducer
            ? localTimezone
            : fallbackTimezone,
        hasLocalSettingsReducer,
    };
};

export { useActiveTimezone };

export const DisplayTimezoneSelect = ({
    isModalVisible,
    setIsModalVisible,
}) => {
    const dispatch = useDispatch();
    const { activeTimezone, hasLocalSettingsReducer } = useActiveTimezone();
    const dispatchTimezoneChange = (val) => {
        const action = { type: 'SET_ACTIVE_TIMEZONE', payload: val };
        if (hasLocalSettingsReducer) {
            dispatch(action);
        } else if (mainStore && typeof mainStore.dispatch === 'function') {
            mainStore.dispatch(action);
        }
    };
    const renderOptions = () => {
        const timezones = moment.tz.names();
        const mappedValues = {};
        const regions = [];

        timezones.map((timezone) => {
            if (
                timezone.includes('/') &&
                !timezone.toLowerCase().includes('us') &&
                !timezone.toLowerCase().includes('etc')
            ) {
                const splitTimezone = timezone.split('/');
                const region = splitTimezone[0];
                if (!mappedValues[region]) {
                    mappedValues[region] = [];
                    regions.push(region);
                }
                mappedValues[region].push(timezone);
            }
        });
        return regions.map((region) => {
            const options = mappedValues[region].map((timezone) => {
                return (
                    <Option key={timezone}>
                        {`(GMT${moment.tz(timezone).format('Z')}) ${timezone}`}
                    </Option>
                );
            });
            return options;
        });
    };

    return (
        <Modal
            visible={isModalVisible}
            footer={null}
            onCancel={() => setIsModalVisible(false)}
            className="tw-rounded-md timezone"
        >
            <div className={styles.timezoneModalContainer}>
                <span className="tw-text-lg tw-font-semibold tw-text-mobileTextBlack tw-mb-6">
                    Change the display timezone
                </span>
                <div className={styles.timezoneSelect}>
                    <label className="tw-text-sm tw-font-normal tw-text-blue-gray">
                        Display all times in
                    </label>
                    <Select
                        style={{ width: '100%' }}
                        showSearch
                        allowClear={false}
                        className="tw-text-base tw-font-normal tw-text-mobileTextBlack"
                        value={activeTimezone}
                        onChange={(val) => {
                            dispatchTimezoneChange(val);
                            setIsModalVisible(false);
                        }}
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                            option.children
                                .toLowerCase()
                                .indexOf(input.toLowerCase()) >= 0
                        }
                        filterSort={(optionA, optionB) =>
                            optionA.children
                                .toLowerCase()
                                .localeCompare(optionB.children.toLowerCase())
                        }
                    >
                        {renderOptions()}
                    </Select>
                    <p className="tw-text-sm tw-font-normal tw-text-blue-gray">
                        Note: Display time zone won’t change the event time
                        zone.
                    </p>
                </div>
            </div>
            <style>{`
            .timezone .ant-modal-content{
            border-radius: 8px !important;
            }
            `}</style>
        </Modal>
    );
};

export const TimezoneLabel = ({
    setIsModalVisible,
    tZstyle = { textAlign: 'right' },
    allowChange = true,
    showLocalTz = false,
}) => {
    const { activeTimezone } = useActiveTimezone();
    const dispTimezone = showLocalTz ? moment.tz.guess() : activeTimezone;
    return (
        <p style={tZstyle} className={styles.timezoneText}>
            All times in:{' '}
            {`(GMT${moment.tz(dispTimezone).format('Z')}) ${dispTimezone}`}
            {allowChange && (
                <span
                    onClick={() => setIsModalVisible(true)}
                    className="tw-text-sm sm:tw-text-xs tw-font-semibold tw-text-mobileTextBlue"
                >
                    Change
                </span>
            )}
        </p>
    );
};
